import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../common/state/hooks";
import {
  getAllSessionsByProjectGroup,
  selectHasDemoSessions,
  sessionsByProjectGroupSelector,
} from "../../state/statsSlice";
import DemoDataBanner from "src/features/onboarding/components/DemoDataBanner";
import { IsLoading } from "../../../../common/components/AppState/IsLoading";
import {
  ANONYMOUS_SESSION_COUNTER_COMPONENT,
  AnonymousSessionCounter,
} from "src/features/subscription/components/AnonymousSessionsCounter";
import {
  fetchAnonymousSessionsCount,
  getNumberOfPaywalledAnonymousSessions,
} from "src/features/subscription/state/subscriptionSlice";

import { ActivityHeatmap } from "src/features/statsNew/components/charts";
import EmptyState from "src/features/statsNew/components/EmptyState";
import SessionsPerMonthChart from "src/features/statsNew/components/charts/SessionsPerMonthChart";
import TopParcoursChart from "src/features/statsNew/components/charts/TopParcoursChart";
import { Link } from "react-router-dom";

import "src/features/statsNew/components/statsNew.css";
import { ChartBarIcon, ChevronRightIcon } from "@heroicons/react/outline";

export const STATS_OVERVIEW_PROJECT_GROUPS_COMPONENT_ID = "statsOverviewProjectGroupsComponent";
export const STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID =
  "statsOverviewProjectGroupsSessionsComponent";
export const BUILDS_COMPONENT_ID = "buildsComponent";

const StatsOverview = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const projectGroupWithSessions = useAppSelector(sessionsByProjectGroupSelector);
  const noEligibleSession = projectGroupWithSessions.length === 0;
  const hasDemoSessions = useAppSelector(selectHasDemoSessions);
  const numberOfPaywalledAnonymousSessions = useAppSelector(getNumberOfPaywalledAnonymousSessions);

  const showAlertBanner = numberOfPaywalledAnonymousSessions > 0;

  const dateRange = { startDate: null, endDate: null };

  const pageSize = showAlertBanner ? 7 : 9;

  const chartContainerClass = `dashboard-chart-container ${showAlertBanner ? "with-alert" : ""}`;

  const totalSessions = useMemo(() => {
    let count = 0;
    projectGroupWithSessions.forEach((group) => {
      group.projects.forEach((project) => {
        count += project.sessions.length;
      });
    });
    return count;
  }, [projectGroupWithSessions]);

  useEffect(() => {
    void dispatch(
      getAllSessionsByProjectGroup({
        componentId: STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID,
        mode: "summary",
      }),
    );
    void dispatch(
      fetchAnonymousSessionsCount({ componentId: ANONYMOUS_SESSION_COUNTER_COMPONENT }),
    );
  }, []);

  return (
    <IsLoading
      componentId={STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID}
      showSuccess={false}
    >
      <div className={`stats-overview-container ${showAlertBanner ? "with-alert" : ""}`}>
        <DemoDataBanner visible={hasDemoSessions} sessionsMode="summary" />
        {showAlertBanner && (
          <div className="w-full mb-2">
            <AnonymousSessionCounter
              numberOfPaywalledAnonymousSessions={numberOfPaywalledAnonymousSessions}
              className="bg-yellow-50 border border-yellow-200 rounded-lg w-full py-2 px-4"
              withSpinner={false}
            />
          </div>
        )}

        {noEligibleSession ? (
          <div className="flex items-center justify-center h-48 bg-white rounded-lg shadow-sm border border-gray-100">
            <EmptyState
              icon="chart"
              title={t("pages.statistics.noData")}
              description={t("onboarding.emptyStates.stats.description")}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2 flex-1">
            <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-2 w-full">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col items-center justify-center p-4 min-h-[140px]">
                <div className="p-3 bg-green-100 rounded-full text-green-600 mb-3">
                  <ChartBarIcon className="h-7 w-7" strokeWidth={2} />
                </div>

                <p className="text-4xl font-bold text-gray-900 leading-none">{totalSessions}</p>

                <p className="text-sm font-medium text-gray-500 mt-2">
                  {t("statsNew.global.totalSessions")}
                </p>

                <div className="w-full border-t border-gray-100 mt-3 pt-3">
                  <Link
                    to="/statistics"
                    className="flex items-center justify-center gap-1 text-sm text-purple-500 hover:text-purple-600 transition-colors"
                  >
                    <span>{t("statsNew.global.viewDetails")}</span>
                    <ChevronRightIcon className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 min-w-0">
                <ActivityHeatmap />
              </div>
            </div>

            <div className="stats-charts-row">
              <div
                className={`w-full bg-white rounded-lg shadow-sm border border-gray-100 p-3 flex flex-col ${chartContainerClass}`}
              >
                <p className="text-gray-800 font-medium text-sm mb-1 flex-shrink-0">
                  {t("statsNew.charts.sessionsPerMonth")}
                </p>
                <div className="flex-1 min-h-0">
                  <SessionsPerMonthChart dateRange={dateRange} />
                </div>
              </div>

              <div
                className={`w-full bg-white rounded-lg shadow-sm border border-gray-100 p-3 flex flex-col ${chartContainerClass}`}
              >
                <p className="text-gray-800 font-medium text-sm mb-1 flex-shrink-0">
                  {t("statsNew.charts.topParcours")}
                </p>
                <div className="flex-1 min-h-0">
                  <TopParcoursChart dateRange={dateRange} pageSize={pageSize} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </IsLoading>
  );
};

export default StatsOverview;
